// ============================================================================
// BOUTON WEB — lit la GAMME partagée `ui-tokens/shell` (sm 30 / md 36 / lg 40) : fin des hauteurs
// 26→40px selon l'app (relevé utilisateur 2026-08-05). Variants aux rôles du thème, couleurs par
// variables — la personnalité d'une app passe par ses overrides de thème, pas par un fork du bouton.
// Adoption PROGRESSIVE, app par app au fil des passes (pas de big-bang sur les écrans existants).
// ============================================================================
import type { CSSProperties, ReactNode } from 'react';
import { BUTTON, BUTTON_WEIGHT, type ButtonSize, type IconName } from '@orgazone/ui-tokens';
import { Icon } from './icons';
import { OZ_INTERACTIVE } from './interactive';
import { t } from '@orgazone/i18n';

export type ButtonVariant = 'primary' | 'solid' | 'ghost' | 'danger' | 'dangerSoft';

export interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  size?: ButtonSize;
  variant?: ButtonVariant;
  icon?: IconName;
  disabled?: boolean;
  /**
   * L'action est DESSINÉE mais pas encore construite : le bouton reste VISIBLE, inactif, et porte
   * « À venir ».
   *
   * ⚠️ RÈGLE DE PLATEFORME (utilisateur, 2026-09-02) : **un bouton non câblé se DIT, il ne
   * disparaît pas**. Une interface à trous laisse croire que la fonction n'existera jamais, ou pire,
   * qu'on l'a oubliée ; une interface complète dont les manques sont NOMMÉS se lit d'un coup d'œil.
   * C'est la branche « se dit » du §23.0 — l'interdit porte sur l'option qui fait SEMBLANT d'aboutir,
   * pas sur celle qui annonce qu'elle n'aboutit pas encore.
   *
   * ⚠️ `aria-disabled` ET NON L'ATTRIBUT `disabled` : un bouton natif désactivé sort du parcours
   * clavier, donc un lecteur d'écran ne l'annonce jamais — et l'information « à venir » serait
   * réservée à ceux qui voient. Il reste donc focalisable, et le clic est avalé.
   */
  soon?: boolean;
  /**
   * POURQUOI c'est inactif — en infobulle, et sur le CONTENEUR.
   * ⚠️ Un `<button disabled>` n'émet aucun événement de souris : son propre `title` ne s'affiche pas
   * dans plusieurs navigateurs. Un `disabled` muet ne dit rien de ce qu'il faut faire pour l'activer.
   */
  reason?: string;
  title?: string;
  /**
   * Le nom du bouton pour une aide technique, quand le texte visible ne suffit pas à dire ce qu'il fait — un nom de
   * gabarit seul, à rétablir.
   * ⚠️ DÉCLARÉ ICI, ET C'EST TOUT LE POINT : TypeScript ne vérifie pas un attribut à trait d'union posé sur un
   *    COMPOSANT — non déclaré, il passait le typage et disparaissait en silence avant d'atteindre le `<button>`.
   */
  'aria-label'?: string;
  /**
   * Un bouton qui reste ENFONCÉ (un filtre allumé) le dit à une aide technique — sa couleur ne s'entend pas (Forme JC).
   * ⚠️ DÉCLARÉ ICI pour la même raison qu'`aria-label` : non déclaré, il passerait le typage et disparaîtrait.
   */
  'aria-pressed'?: boolean;
  type?: 'button' | 'submit';
  style?: CSSProperties;
}

/**
 * ⚠️ `primary` et `ghost` N'ONT PLUS DE PALETTE EN LIGNE : ils portent les classes de la charte.
 *
 * Un style EN LIGNE ne peut pas porter de `:hover` — c'est structurel. Ces deux variantes étaient donc
 * PLATES partout où ce composant sert (EDI, Store), pendant que la Console se réécrivait un survol
 * partiel dans sa propre feuille. La recette vit désormais une seule fois, dans la couche
 * `orgazone-charte` (`.btn-cta` / `.btn-ghost`), et reste surchargeable par n'importe quelle app.
 *
 * `danger` et `dangerSoft` gardent leur palette en ligne : ce sont des variantes RARES, sans recette
 * de survol dans la maquette — leur en inventer une ici serait ajouter une règle que personne n'a
 * dessinée. Elles héritent du retour visuel générique d'`OZ_INTERACTIVE`.
 */
/**
 * ⚠️ DEUX BOUTONS D'ACTION, ET C'EST VOULU LE TEMPS DE L'ARBITRAGE (demande utilisateur 2026-09-07).
 * `primary` est le bouton de la charte : dégradé, encre sombre, relief. `solid` est celui de la
 * MAQUETTE du back-office, où il apparaît **77 fois** : aplat, texte blanc, sans ombre, et un survol
 * qui FONCE. Trois de nos écrans le recopiaient déjà à la main, valeurs en dur — un dessin employé
 * partout et absent du jeu partagé finit toujours recopié.
 * ⚠️ `solid` mesure **2,10 : 1** de contraste (blanc sur le cran 500) là où 13,5 px en demandent 4,5.
 * Il est fourni tel que la maquette le dessine, pour comparer à l'écran ; le cran de fond reste à
 * trancher, et la planche affiche la mesure sous le bouton.
 */
const CLASSE: Partial<Record<ButtonVariant, string>> = {
  primary: 'btn-cta',
  solid: 'btn-solid',
  ghost: 'btn-ghost',
};

/**
 * La bordure rose de la charte — celle du bouton `dangerSoft`, reprise par les bandeaux d'échec des écrans.
 * ⚠️ UN MÉLANGE, PAS UNE COULEUR ÉCRITE : elle suit le thème sombre avec la teinte qu'elle éclaircit.
 */
export const DANGER_SOFT_BORDER = 'color-mix(in srgb, var(--rose) 40%, transparent)';

const PALETTE: Partial<Record<ButtonVariant, CSSProperties>> = {
  danger: { background: 'var(--danger)', color: '#fff', border: '1px solid var(--danger)' },
  dangerSoft: { background: 'var(--rose-bg)', color: 'var(--rose-fg)', border: `1px solid ${DANGER_SOFT_BORDER}` },
};

export function Button({
  children, onClick, size = 'md', variant = 'ghost', icon, disabled, soon, reason, title,
  type = 'button', style, 'aria-label': ariaLabel, 'aria-pressed': ariaPressed,
}: ButtonProps) {
  const s = BUTTON[size];
  const inactif = Boolean(disabled) || Boolean(soon);
  // Le motif l'emporte sur le titre : c'est lui qui explique l'inactivité.
  const infobulle = reason ?? title ?? (soon ? t('Pas encore construit.') : undefined);

  const bouton = (
    <button
      type={type}
      className={[OZ_INTERACTIVE, CLASSE[variant]].filter(Boolean).join(' ')}
      // ⚠️ Le clic est AVALÉ plutôt que le bouton désactivé quand il s'agit d'un « à venir » :
      //    voir `soon`. Un `disabled` natif le retirerait du parcours clavier.
      onClick={inactif ? undefined : onClick}
      disabled={disabled && !soon}
      aria-disabled={inactif ? true : undefined}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      title={infobulle}
      // ⚠️ La GÉOMÉTRIE reste en ligne (la taille est un choix d'appel, pas de charte) et passe APRÈS
      // la classe : `size` doit continuer de commander la hauteur, sinon un « sm » sortirait à 36 px.
      style={{
        height: s.height, padding: `0 ${s.padH}px`, borderRadius: s.radius, fontSize: s.fontSize,
        fontWeight: BUTTON_WEIGHT, display: 'inline-flex', alignItems: 'center', gap: 7,
        cursor: inactif ? 'not-allowed' : 'pointer', opacity: inactif ? 0.5 : 1,
        ...PALETTE[variant],
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={Math.round(s.fontSize) + 2} />}
      {children}
      {/* ⚠️ LA MENTION EST DANS LE BOUTON, pas à côté : une pastille voisine se perd au premier
          changement de mise en page, et l'état « à venir » doit voyager AVEC l'action. */}
      {soon && (
        <span style={{
          marginLeft: 4, padding: '1px 6px', borderRadius: 'var(--r-pill)', fontSize: Math.max(10, s.fontSize - 3),
          fontWeight: 600, background: 'var(--ink-100)', color: 'var(--ink-500)', whiteSpace: 'nowrap',
        }}>{t('à venir')}</span>
      )}
    </button>
  );

  // ⚠️ L'INFOBULLE VIT SUR UN CONTENEUR quand le bouton est NATIVEMENT désactivé : sans événement
  //    de souris, son propre `title` ne s'affiche pas. Sans ce span, un `disabled` resterait muet.
  return disabled && !soon && infobulle
    ? <span title={infobulle} style={{ display: 'inline-flex' }}>{bouton}</span>
    : bouton;
}
